/**
 * Dashboard Onboarding Content — State 2 (provisioned, setup incomplete)
 *
 * Renders: IntentPathCard (3 goal-oriented paths with accordion) +
 * storefront banner + news. Replaces the old ERP-jargon checklist.
 */

import { useState, useMemo } from "react";
import { IntentPathCard } from "./IntentPathCard";
import { DashboardStorefrontBanner } from "./DashboardStorefrontBanner";
import { DashboardNews } from "./DashboardNews";
import { useChatwootOpened } from "../hooks/useChatwootOpened";
import type { IntentPath } from "@/hooks/useDashboardData";

interface IntentPathLabelsInput {
  question: string;
  paths: Record<string, { title: string; description: string }>;
  steps: Record<string, { title: string; description: string }>;
  exploreAll: string;
  completed: string;
}

interface DashboardOnboardingContentProps {
  paths: IntentPath[];
  intentLabels: IntentPathLabelsInput;
  storefrontUrl: string | null;
  storefrontLabels: {
    yourStorefront: string;
    openStorefront: string;
    copyUrl: string;
  };
  newsLabels: {
    news: string;
    newFeature: string;
    siteCanvas: string;
    siteCanvasDescription: string;
    trySiteCanvas: string;
  };
  /** Auto-select this path based on signup intent (Epic 14 Phase 7) */
  signupIntent?: string | null;
}

export function DashboardOnboardingContent({
  paths,
  intentLabels,
  storefrontUrl,
  storefrontLabels,
  newsLabels,
  signupIntent,
}: DashboardOnboardingContentProps) {
  // Auto-select path matching signup intent (Epic 14 Phase 7)
  const [selectedPathId, setSelectedPathId] = useState<string | null>(
    signupIntent ?? null
  );
  const chatwootOpened = useChatwootOpened();

  // Merge the localStorage-based Chatwoot step into the paths from the API
  const mergedPaths = useMemo(() => {
    if (!chatwootOpened) return paths;
    return paths.map((path) => {
      if (path.id !== "chat") return path;
      const updatedSteps = path.steps.map((step) =>
        step.id === "open-inbox" ? { ...step, completed: true } : step
      );
      const allComplete = updatedSteps.every((s) => s.completed);
      return { ...path, steps: updatedSteps, allComplete };
    });
  }, [paths, chatwootOpened]);

  return (
    <>
      {/* Intent Path Card */}
      <IntentPathCard
        paths={mergedPaths}
        selectedPathId={selectedPathId}
        onSelectPath={setSelectedPathId}
        labels={intentLabels}
      />

      {/* Storefront Banner */}
      {storefrontUrl && (
        <DashboardStorefrontBanner
          storefrontUrl={storefrontUrl}
          labels={storefrontLabels}
        />
      )}

      {/* News — full hero variant */}
      <DashboardNews labels={newsLabels} />
    </>
  );
}
